// Router integration (PRD §3.1). Mount <AnalyticsRouter /> once inside the Router tree
// (App.jsx). On every route entry it:
//   1. flushes the previous screen's engaged time (active_ms / total_ms)
//   2. resets the engagement accumulator for the new screen
//   3. emits screen_view
//
// Route changes are DEBOUNCED: redirect chains (logout → login → / → home) fire several
// changes in milliseconds, none of which is a screen the user actually saw. We only commit
// a route that sticks for `screenDebounceMs`, so those transient hops never emit.
//
// It renders nothing and holds no React state that would re-render the tree.

import { useEffect, useRef } from "react"
import { useLocation } from "react-router-dom"
import { trackScreenView, trackTimeOnScreen } from "./index"
import { enterScreen, leaveScreen } from "./engagedTime"
import { config } from "./config"

function screenFromPath(pathname) {
    const seg = (pathname || "").split("/").filter(Boolean)[0]
    return seg || "home"
}

export default function AnalyticsRouter() {
    const location = useLocation()
    const prev = useRef(null)   // last COMMITTED { path, screen }
    const timer = useRef(null)

    useEffect(() => {
        const path = location.pathname
        const screen = screenFromPath(path)

        timer.current = setTimeout(() => {
            timer.current = null
            if (prev.current?.path === path) return // route didn't actually change

            // Close out the screen we're leaving.
            if (prev.current) {
                const { active_ms, total_ms } = leaveScreen()
                trackTimeOnScreen({ screen: prev.current.screen, active_ms, total_ms })
            }

            // Enter the new one.
            enterScreen()
            trackScreenView({ path, screen, referrer: prev.current?.path ?? null })
            prev.current = { path, screen }
        }, config.screenDebounceMs)

        // Cancel the pending commit if another route change arrives first (→ debounce).
        return () => { if (timer.current) clearTimeout(timer.current) }
    }, [location.pathname])

    return null
}
